import { getUserListingApi } from '../../util/api';
import * as log from '../../util/log';

// Tier 1.2 (Sharetribe disconnect, Phase 6) originally only skipped the live
// sdk.ownListings.show/publishDraft calls below for coaches whose
// UserListing doc was auto-created via createNativeListingIdForUser
// (server/customFlow/controller/UserListingController.js) -- a plain Mongo
// ObjectId as their listingId, with no real Sharetribe listing behind it at
// all. Since then, Marketplace API credentials were revoked entirely, so a
// real, legacy Sharetribe-shaped listingId now fails those calls just as
// reliably as a native one always 404d -- which SessionTypes.js's
// fetchListingError branch turns into a bare "Error loading listing"
// screen that replaces the entire tab, blocking every real coach (not just
// native-listingId ones) from managing session types at all. Nothing this
// tab needs from a live listing survives credential revocation either way
// (cover image lives on the coach's profile, title/description come from
// getListingContentApi, both already Mongo-native), so both live calls
// were removed outright rather than kept behind an id-shape check that no
// longer predicts reachability.

// ================ Action types ================ //

export const FETCH_USER_LISTING_REQUEST = 'app/SessionTypes/FETCH_USER_LISTING_REQUEST';
export const FETCH_USER_LISTING_SUCCESS = 'app/SessionTypes/FETCH_USER_LISTING_SUCCESS';
export const FETCH_USER_LISTING_ERROR = 'app/SessionTypes/FETCH_USER_LISTING_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/SessionTypes/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/SessionTypes/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/SessionTypes/PUBLISH_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  listingId: null,
  fetchListingInProgress: false,
  fetchListingError: null,
  publishListingInProgress: false,
  publishListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_USER_LISTING_REQUEST:
      return { ...state, fetchListingInProgress: true, fetchListingError: null };
    case FETCH_USER_LISTING_SUCCESS:
      return {
        ...state,
        fetchListingInProgress: false,
        listingId: payload.listingId,
      };
    case FETCH_USER_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    case PUBLISH_LISTING_REQUEST:
      return { ...state, publishListingInProgress: true, publishListingError: null };
    case PUBLISH_LISTING_SUCCESS:
      return { ...state, publishListingInProgress: false };
    case PUBLISH_LISTING_ERROR:
      return { ...state, publishListingInProgress: false, publishListingError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchUserListingRequest = () => ({ type: FETCH_USER_LISTING_REQUEST });
export const fetchUserListingSuccess = (listingId) => ({
  type: FETCH_USER_LISTING_SUCCESS,
  payload: { listingId },
});
export const fetchUserListingError = error => ({
  type: FETCH_USER_LISTING_ERROR,
  payload: error,
  error: true,
});

export const publishListingRequest = () => ({ type: PUBLISH_LISTING_REQUEST });
export const publishListingSuccess = () => ({
  type: PUBLISH_LISTING_SUCCESS,
  payload: {},
});
export const publishListingError = error => ({
  type: PUBLISH_LISTING_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

/**
 * Fetch user's listing id from MongoDB. No live Sharetribe listing fetch --
 * see the file-level comment above for why.
 */
export const fetchUserListing = () => dispatch => {
  dispatch(fetchUserListingRequest());

  return getUserListingApi({})
    .then(response => {
      const listingId = response.data.listingId;

      if (!listingId) {
        throw new Error('No listing found for user');
      }

      dispatch(fetchUserListingSuccess(listingId));
      return null;
    })
    .catch(e => {
      log.error(e, 'fetch-user-listing-failed');
      dispatch(fetchUserListingError(e));
    });
};

/**
 * Publish listing -- no-op. There's no Sharetribe draft to publish for any
 * coach anymore; see the file-level comment above for why.
 */
export const publishListing = () => dispatch => {
  dispatch(publishListingRequest());
  dispatch(publishListingSuccess());
  return Promise.resolve(null);
};
